import {useMemo} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {ROUTES} from '@/config/routesConstants';
import {
  CREATOR_STATUS,
  type CreatorStatus,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import {
  PROFILE_SOURCE,
  type ProfileSource,
} from '@/components/creator-profile/config/profileSources';

/**
 * Override read from `?source=`. Used by modal openers (Assets, Statistics,
 * Invited) that share a URL pattern and can't otherwise be disambiguated.
 */
export const PROFILE_SOURCE_QUERY_PARAM = 'source';

interface UseProfileSourceParams {
  applicantStatus?: CreatorStatus | null;
  displayPublicDataOnly?: boolean;
}

/**
 * Resolution order:
 *   1. `?source=` query param (when it maps to a known source).
 *   2. `/creator-crm/` path → CRM.
 *   3. `/campaigns-v2/` path → refined by `applicantStatus` (Applications /
 *      Accepted / Declined / Invited). Returns null when no status is provided
 *      so the action row hides until the campaign relationship resolves.
 *   4. Otherwise → STANDALONE.
 *
 * Returns null also when `displayPublicDataOnly` is set (public profile,
 * shared list — no action row at all).
 */
function useProfileSource({
  applicantStatus,
  displayPublicDataOnly,
}: UseProfileSourceParams = {}): ProfileSource | null {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const overrideParam = searchParams.get(PROFILE_SOURCE_QUERY_PARAM);

  return useMemo(() => {
    if (displayPublicDataOnly) {
      return null;
    }

    if (overrideParam) {
      const overrideSource = resolveOverrideParam(overrideParam);
      if (overrideSource) {
        return overrideSource;
      }
    }

    const pathname = location.pathname.toLowerCase();

    if (pathname.startsWith(`${ROUTES.CREATOR_CRM.toLowerCase()}/`)) {
      return PROFILE_SOURCE.CRM;
    }

    if (pathname.startsWith(`${ROUTES.CAMPAIGNS_V2.toLowerCase()}/`)) {
      return resolveCampaignSource(applicantStatus);
    }

    return PROFILE_SOURCE.STANDALONE;
  }, [
    location.pathname,
    overrideParam,
    applicantStatus,
    displayPublicDataOnly,
  ]);
}

function resolveOverrideParam(value: string): ProfileSource | null {
  const normalized = value.toLowerCase();
  const knownSources = Object.values(PROFILE_SOURCE) as string[];
  if (knownSources.includes(normalized)) {
    return normalized as ProfileSource;
  }
  return null;
}

function resolveCampaignSource(
  applicantStatus?: CreatorStatus | null
): ProfileSource | null {
  switch (applicantStatus) {
    case CREATOR_STATUS.Applications:
      return PROFILE_SOURCE.APPLICATIONS;
    case CREATOR_STATUS.Hired:
      return PROFILE_SOURCE.ACCEPTED;
    case CREATOR_STATUS.Declined:
      return PROFILE_SOURCE.DECLINED;
    case CREATOR_STATUS.Invited:
      return PROFILE_SOURCE.INVITED;
    default:
      // Hide the action row rather than surface STANDALONE CTAs (e.g.
      // "Invite to campaign") inside a campaign view.
      return null;
  }
}

export default useProfileSource;
